// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Checkbox, Space, Text } from '@mantine/core';
import { FC } from 'react';
import { IconCheckbox, IconFileSymlink, IconTrash } from '@tabler/icons';
import { useLocation } from 'react-router-dom';
import { AgreementDTO } from '../../Types/DocumentT';
import { BaseInput } from '../../Organismns/tiptap/TiptapEditorInput';
import DocumentInputToolbar from '../../Organismns/tiptap/toolbars/DocumentInputToolbar';
import PanePaper from '../PanePaper';
import { openFilePickerModal } from '../Dialogs/FilePickerModal';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import { AgreementS } from '../../Service/AgreementS';
import { FileS } from '../../Service/FileS';
import BookitupEntityPreview from '../../Atoms/BookitupEntityPreview';
import { TiptapS } from '../../Service/TiptapS';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';

interface Props {
  index: number;
  agreement: AgreementDTO;
  remove: (agreementId: string) => void;
  patch: (agreement: AgreementDTO) => void;
  documentId: number;
}

const Agreement: FC<Props> = ({ index, agreement, patch, remove, documentId }) => {
  const { id: agreementId, content, required, entityId, entityType, fileName } = agreement;
  const { profileId } = useJwtClaims();
  const { pathname } = useLocation();
  const fileAttached = fileName && content && content.includes(fileName);
  const isTemplate = pathname.includes('settings');

  const removeHandler = async () => {
    openDeleteConfirmModal({
      title: 'Einwilligung löschen?',
      description: 'Möchtest Du die Einwilligung wirklich löschen?',
      onConfirm: async () => {
        const { remove: removeFromDocument, removeFromTemplate } = AgreementS;
        const handler = isTemplate ? removeFromTemplate : removeFromDocument;
        const removed = await handler(agreementId, documentId);
        if (removed) {
          remove(agreementId);
        }
        return removed;
      },
    });
  };

  const patchHandler = async (body: Partial<AgreementDTO>) => {
    const resJSON = await AgreementS.update(agreementId, body, isTemplate ? undefined : documentId);
    if (resJSON) {
      patch(resJSON);
      return true;
    }
    return false;
  };

  return (
    <PanePaper
      rightSection={
        <ActionIcon>
          <IconTrash size={20} onClick={removeHandler} />
        </ActionIcon>
      }
      sx={{ borderColor: '#D4D4D4' }}
    >
      <Text mb="md" size="md" weight="bolder">
        <IconCheckbox size={20} style={{ marginRight: 10, marginTop: -3 }} />
        {`${index + 1}. Einwilligungen`}
      </Text>
      {!entityId && (
        <Text
          display="inline-block"
          mt="xs"
          color="blue"
          variant="link"
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            openFilePickerModal({
              profileId: profileId.toString(),
              onSelect: patchHandler,
              allowedExtensions: ['pdf'],
              accept: 'application/pdf',
              entityType: 'public',
            })
          }
        >
          + PDF anhängen
        </Text>
      )}
      {entityId && (
        <>
          <BookitupEntityPreview
            entityId={entityId}
            entityType={entityType}
            filename={fileName ?? ''}
            onDelete={() => patchHandler({ entityId: null, entityType: null, fileName: null })}
          />
          {!fileAttached && (
            <Text mt={5} size="xs" color="dimmed">
              Verknüpfen Sie Ihre PDF-Datei ganz einfach mit <IconFileSymlink size={16} style={{ marginBottom: 2 }} />{' '}
              dem Symbol
            </Text>
          )}
        </>
      )}
      <Space h="xl" />
      <BaseInput
        label="Inhalt"
        value={content}
        patchField={(html) => patchHandler({ content: html })}
        toolbar={
          <DocumentInputToolbar
            showHeading={false}
            includePlaceholers={false}
            fileName={fileName}
            fileUrl={FileS.getPublicUrl(profileId, fileName ?? '')}
            showFileIcon={!fileAttached}
          />
        }
        extensions={TiptapS.Extentions.MINIMAL_AGREEMENTS_INPUT}
      />
      <Checkbox
        mt="md"
        checked={required}
        label="Einwilligung erforderlich"
        onChange={(e) => patchHandler({ required: e.currentTarget.checked })}
      />
    </PanePaper>
  );
};

export default Agreement;
